import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const SECTIONS = [
  {
    id: "shipping", title: "Shipping & Delivery Policy",
    body: [
      "GameRealm sells digital products only. Nothing is shipped physically and no shipping fees apply.",
      "Activation codes are delivered to the email address entered at checkout after our team verifies your USDT transaction on the blockchain. Most orders are delivered within minutes; some can take up to 24 hours depending on network confirmations and verification.",
      "You must submit your transaction hash on the checkout page within 5 minutes of starting a payment. Orders without a hash in that window expire and are not counted as paid.",
      "Please check your spam or promotions folder. If your code hasn't arrived within 24 hours, contact us through the live chat with your order ID.",
      "You are responsible for entering a correct email address. We are not responsible for codes delivered to a mistyped email.",
    ],
  },
  {
    id: "refund", title: "Refund Policy",
    body: [
      "All sales are final. GameRealm does not offer refunds for any purchase, including single game keys, offers and mystery boxes.",
      "Crypto payments cannot be reversed. Payments sent to the wrong network, the wrong address or for the wrong amount cannot be refunded.",
      "If a key you received is invalid or already used, report it through the live chat within 48 hours of delivery, with your order ID and a screenshot of the activation error. After checking, we will replace it with a working key for the same product. A replacement is the only remedy offered, never a cash or crypto refund.",
      "Mystery box contents are random and titles can repeat. Receiving games you already own or don't want is not grounds for a refund or replacement.",
      "Region or platform issues caused by activating on the wrong platform or account are not covered.",
    ],
  },
  {
    id: "return", title: "Return Policy",
    body: [
      "Digital activation codes cannot be returned once they have been delivered, because a code can be copied or redeemed the moment it is seen.",
      "We do not accept returns or exchanges for any product, including unused or unopened codes.",
      "Please check the product name and platform (Steam, Rockstar, Xbox, Epic and others) carefully before paying.",
    ],
  },
  {
    id: "fraud", title: "Fake Payments & Account Bans",
    body: [
      "Every transaction hash is checked against our wallets on the blockchain before a code is delivered.",
      "Submitting a fake, reused, incomplete or someone else's transaction hash is fraud. If we detect it, the order is cancelled and the email address and connection used are permanently banned from GameRealm, including checkout and live chat.",
      "Banned customers will not receive any codes, and attempts to get around a ban (new emails, VPNs and similar) will also be banned.",
      "If you believe you were banned by mistake, you can appeal by sending a valid transaction hash for the order to our support team. Decisions on appeals are final.",
    ],
  },
  {
    id: "terms", title: "General Terms",
    body: [
      "By placing an order you agree to these policies. Prices are listed in USD and paid in USDT at a 1:1 rate.",
      "GameRealm may change prices, stock and these policies at any time. The policies in effect on the day of your order apply.",
      "Game names and trademarks belong to their respective owners. GameRealm is an independent reseller and is not affiliated with Valve, Rockstar Games, Microsoft or any publisher.",
    ],
  },
];

export default function Policies() {
  const { hash } = useLocation();
  useSEO({ title: "Shipping, Refund & Return Policies", description: "GameRealm delivery, no-refund, no-return and fake-payment ban policies for digital game keys paid with USDT.", path: "/policies" });
  useEffect(() => { if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, [hash]);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-10 space-y-10">
      <div className="rise">
        <p className="label-eyebrow text-emerald-400">Policies</p>
        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl mt-3">Store policies</h1>
        <p className="text-gray-400 mt-4 text-sm">Last updated {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        <nav className="flex flex-wrap gap-2 mt-6">
          {SECTIONS.map((s) => <Link key={s.id} to={`/policies#${s.id}`} data-testid={`policy-nav-${s.id}`} className="text-sm px-3 py-2.5 sm:py-1.5 rounded-md border border-white/10 hover:border-emerald-500/60">{s.title.replace(" Policy", "")}</Link>)}
        </nav>
      </div>
      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} data-testid={`policy-${s.id}`} className="scroll-mt-24 rounded-xl border border-white/10 bg-[#0F1117] p-6 sm:p-8">
          <h2 className="font-display font-semibold tracking-tight text-xl sm:text-2xl">{s.title}</h2>
          <ul className="mt-4 space-y-3 text-gray-300 text-sm leading-relaxed list-disc pl-5">{s.body.map((b) => <li key={b}>{b}</li>)}</ul>
        </section>
      ))}
    </div>
  );
}
